import {
  Users,
  Info,
  Plus,
  Trash2,
  Globe,
  TrendingUp,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Target,
  Briefcase,
  Brain,
  Building2,
} from 'lucide-react';
import { useState } from 'react';
import CompetitiveAnalysisChart from '../../../components/CompetitiveAnalysisChart';
import {
  isCompetitorResearchStale,
  type CompetitorAIInsights,
} from '../../../utils/competitorResearch';

interface Competitor {
  id: string;
  name: string;
  description?: string;
  website?: string;
  priceQuality: 'low' | 'medium' | 'high' | 'unknown';
  geoScope: 'local' | 'regional' | 'national' | 'international' | 'unknown';
  marketSize: number | null;
  strategy?: string;
  capabilities?: string;
  objectives?: string;
  assumptions?: string;
  revenue?: number;
  employees?: number;
  foundedYear?: number;
  priority?: 'low' | 'medium' | 'high';
  lastScrapedAt?: string;
  // Additional analysis fields
  investments?: string;
  marketPosition?: string;
  capabilityAnalysis?: string;
  aiInsights?: CompetitorAIInsights | null;
}

interface CompetitorsSectionProps {
  companyId: string;
  externalData: {
    competitors: Competitor[];
  };
  competitorsProgress: number;
  onAddCompetitor: () => void;
  onUpdateCompetitor: (id: string, field: string, value: any) => void;
  onRemoveCompetitor: (id: string) => void;
  onRefetchCompetitors: () => Promise<void>;
  onInputFocus: () => void;
  onInputBlur: () => void;
  onInputChange: () => void;
}

export default function CompetitorsSection({
  companyId,
  externalData,
  competitorsProgress,
  onAddCompetitor,
  onUpdateCompetitor,
  onRemoveCompetitor,
  onRefetchCompetitors,
  onInputFocus,
  onInputBlur,
  onInputChange,
}: CompetitorsSectionProps) {
  const [expandedCompetitor, setExpandedCompetitor] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'analysis'>('overview');
  const [analyzingCompetitorId, setAnalyzingCompetitorId] = useState<string | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analysisSuccess, setAnalysisSuccess] = useState<string | null>(null);

  const toggleCompetitorExpansion = (competitorId: string) => {
    setExpandedCompetitor(expandedCompetitor === competitorId ? null : competitorId);
  };

  const handleAnalyzeCompetitor = async (competitor: Competitor) => {
    setAnalyzingCompetitorId(competitor.id);
    setAnalysisError(null);
    setAnalysisSuccess(null);

    try {
      const { competitorsApi } = await import('../../../services/api');
      const response = await competitorsApi.scrapeCompetitor(competitor.id);

      if (response.success) {
        // Refetch all competitors to get the updated data
        await onRefetchCompetitors();
        setAnalysisSuccess(`Analysis complete for ${competitor.name}`);

        // Clear success message after 5 seconds
        setTimeout(() => {
          setAnalysisSuccess(null);
        }, 5000);
      } else {
        setAnalysisError(response.error || 'Analysis failed. Please try again.');
      }
    } catch (error: any) {
      console.error('Failed to analyze competitor:', error);
      const errorMessage =
        error?.response?.data?.error || error?.message || 'Analysis failed. Please try again.';
      setAnalysisError(errorMessage);
    } finally {
      setAnalyzingCompetitorId(null);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 rounded-xl bg-purple-100">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Competitive Landscape</h2>
              <p className="text-gray-600 mt-1">
                Analyze your main competitors and market positioning
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-right">
              <div className="text-sm font-medium text-gray-500">Completion</div>
              <div
                className={`text-2xl font-bold ${competitorsProgress >= 70 ? 'text-green-600' : 'text-orange-600'}`}
              >
                {competitorsProgress}%
              </div>
            </div>
            <button
              onClick={onAddCompetitor}
              className="bg-purple-600 text-white px-6 py-3 rounded-xl hover:bg-purple-700 flex items-center space-x-2 font-medium shadow-sm transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span>Add Competitor</span>
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-4 w-4" />
                <span>Overview & Analysis</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('analysis')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analysis'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Brain className="h-4 w-4" />
                <span>Detailed Analysis</span>
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'overview' ? (
          <div className="space-y-6">
            {/* Info Banner */}
            <div className="bg-purple-50 rounded-xl border border-purple-200 p-4">
              <p className="text-purple-800 flex items-center">
                <Info className="h-4 w-4 mr-2 flex-shrink-0" />
                Add your main competitors and analyze their market positioning. Include website URLs
                for AI-powered competitive intelligence.
              </p>
            </div>

            {/* Success/Error Messages */}
            {analysisSuccess && (
              <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                <p className="text-green-800 flex items-center">
                  <Brain className="h-4 w-4 mr-2 flex-shrink-0" />
                  {analysisSuccess}
                </p>
              </div>
            )}

            {analysisError && (
              <div className="bg-red-50 rounded-xl border border-red-200 p-4">
                <p className="text-red-800 flex items-center">
                  <Info className="h-4 w-4 mr-2 flex-shrink-0" />
                  {analysisError}
                </p>
              </div>
            )}

            {/* Competitors Grid */}
            {externalData.competitors.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No competitors added yet
                </h3>
                <p className="text-gray-500 mb-6">
                  Start building your competitive landscape analysis
                </p>
                <button
                  onClick={onAddCompetitor}
                  className="bg-purple-600 text-white px-6 py-3 rounded-xl hover:bg-purple-700 flex items-center space-x-2 font-medium mx-auto"
                >
                  <Plus className="h-5 w-5" />
                  <span>Add Your First Competitor</span>
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  {externalData.competitors.map((competitor) => (
                    <CompetitorCard
                      key={competitor.id}
                      competitor={competitor}
                      companyId={companyId}
                      onUpdateCompetitor={onUpdateCompetitor}
                      onRemoveCompetitor={onRemoveCompetitor}
                      onAnalyze={handleAnalyzeCompetitor}
                      isAnalyzing={analyzingCompetitorId === competitor.id}
                      onInputFocus={onInputFocus}
                      onInputBlur={onInputBlur}
                      onInputChange={onInputChange}
                    />
                  ))}
                </div>

                {/* Competitive Analysis Chart at Bottom */}
                <CompetitiveAnalysisChart
                  competitors={externalData.competitors}
                  className="shadow-sm"
                />
              </div>
            )}
          </div>
        ) : (
          <DetailedAnalysisTab
            competitors={externalData.competitors}
            companyId={companyId}
            expandedCompetitor={expandedCompetitor}
            onToggleExpansion={toggleCompetitorExpansion}
            onUpdateCompetitor={onUpdateCompetitor}
            onInputFocus={onInputFocus}
            onInputBlur={onInputBlur}
            onInputChange={onInputChange}
          />
        )}
      </div>
    </div>
  );
}

// Competitor Card Component
function CompetitorCard({
  competitor,
  companyId,
  onUpdateCompetitor,
  onRemoveCompetitor,
  onAnalyze,
  isAnalyzing,
  onInputFocus,
  onInputBlur,
  onInputChange,
}: {
  competitor: Competitor;
  companyId: string;
  onUpdateCompetitor: (id: string, field: string, value: any) => void;
  onRemoveCompetitor: (id: string) => void;
  onAnalyze: (competitor: Competitor) => void;
  isAnalyzing: boolean;
  onInputFocus: () => void;
  onInputBlur: () => void;
  onInputChange: () => void;
}) {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="p-6">
        {/* Header with Name and Priority */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="p-2 rounded-lg bg-gray-100">
              <Building2 className="h-5 w-5 text-gray-600" />
            </div>
            <div className="flex-1">
              <input
                type="text"
                value={competitor.name}
                onChange={(e) => {
                  onUpdateCompetitor(competitor.id, 'name', e.target.value);
                  onInputChange();
                }}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                className="text-xl font-bold text-gray-900 bg-transparent border-none p-0 focus:ring-0 focus:outline-none w-full"
                placeholder="Competitor Name"
              />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(competitor.priority || 'medium')}`}
            >
              {(competitor.priority || 'medium').charAt(0).toUpperCase() +
                (competitor.priority || 'medium').slice(1)}{' '}
              Priority
            </span>
            <button
              onClick={() => onRemoveCompetitor(competitor.id)}
              className="text-gray-400 hover:text-red-600 p-1 rounded-lg hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <CompetitorResearchEvidence competitor={competitor} companyId={companyId} />

        {/* Basic Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Website URL</label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="url"
                value={competitor.website || ''}
                onChange={(e) => {
                  onUpdateCompetitor(competitor.id, 'website', e.target.value);
                  onInputChange();
                }}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                placeholder="https://competitor.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price/Quality Level
            </label>
            <select
              value={competitor.priceQuality}
              onChange={(e) => onUpdateCompetitor(competitor.id, 'priceQuality', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
            >
              <option value="unknown">Not assessed</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Geographic Scope</label>
            <select
              value={competitor.geoScope}
              onChange={(e) => onUpdateCompetitor(competitor.id, 'geoScope', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
            >
              <option value="unknown">Not assessed</option>
              <option value="local">Local</option>
              <option value="regional">Regional</option>
              <option value="national">National</option>
              <option value="international">International</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Market Share (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              value={competitor.marketSize ?? ''}
              onChange={(e) => {
                onUpdateCompetitor(
                  competitor.id,
                  'marketSize',
                  e.target.value === '' ? null : Number(e.target.value)
                );
                onInputChange();
              }}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              placeholder="Unknown"
            />
          </div>
        </div>

        {/* Quick Strategy Overview */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quick Strategy Overview
          </label>
          <textarea
            value={competitor.strategy || ''}
            onChange={(e) => {
              onUpdateCompetitor(competitor.id, 'strategy', e.target.value);
              onInputChange();
            }}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
            rows={2}
            placeholder="Brief overview of their competitive approach..."
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-100">
          <div className="flex items-center space-x-4">
            {competitor.website && (
              <button
                onClick={() => onAnalyze(competitor)}
                disabled={isAnalyzing}
                className={`text-sm font-medium flex items-center space-x-1 px-3 py-1 rounded-lg ${
                  isAnalyzing
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                }`}
              >
                {isAnalyzing ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                    <span>Analyzing...</span>
                  </>
                ) : (
                  <>
                    <TrendingUp className="h-4 w-4" />
                    <span>AI Analyze</span>
                  </>
                )}
              </button>
            )}
            {competitor.lastScrapedAt && !isAnalyzing && (
              <span className="text-xs text-gray-500">
                Last analyzed: {new Date(competitor.lastScrapedAt).toLocaleDateString()}
              </span>
            )}
          </div>
          <select
            value={competitor.priority || 'medium'}
            onChange={(e) => onUpdateCompetitor(competitor.id, 'priority', e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="low">Low Priority</option>
            <option value="medium">Medium Priority</option>
            <option value="high">High Priority</option>
          </select>
        </div>
      </div>
    </div>
  );
}

// Detailed Analysis Tab Component
function DetailedAnalysisTab({
  competitors,
  companyId,
  expandedCompetitor,
  onToggleExpansion,
  onUpdateCompetitor,
  onInputFocus,
  onInputBlur,
  onInputChange,
}: {
  competitors: Competitor[];
  companyId: string;
  expandedCompetitor: string | null;
  onToggleExpansion: (id: string) => void;
  onUpdateCompetitor: (id: string, field: string, value: any) => void;
  onInputFocus: () => void;
  onInputBlur: () => void;
  onInputChange: () => void;
}) {
  if (competitors.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Brain className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No competitors to analyze</h3>
        <p className="text-gray-500">
          Add competitors in the Overview tab to perform detailed analysis
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
        <p className="text-blue-800 flex items-center">
          <Brain className="h-4 w-4 mr-2 flex-shrink-0" />
          Conduct in-depth competitive analysis using the four key areas: Strategy, Objectives,
          Capabilities, and Assumptions.
        </p>
      </div>

      {competitors.map((competitor) => (
        <div key={competitor.id} className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <button
            onClick={() => onToggleExpansion(competitor.id)}
            className="w-full p-6 text-left hover:bg-gray-50 rounded-xl transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-2 rounded-lg bg-gray-100">
                  <Building2 className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{competitor.name}</h3>
                  <p className="text-sm text-gray-500">
                    {competitor.priceQuality === 'unknown'
                      ? 'Price/quality not assessed'
                      : `${competitor.priceQuality} price/quality`}{' '}
                    •{' '}
                    {competitor.geoScope === 'unknown'
                      ? 'Geographic scope not assessed'
                      : `${competitor.geoScope} scope`}{' '}
                    •{' '}
                    {competitor.marketSize === null
                      ? 'Market share unknown'
                      : `${competitor.marketSize}% market share`}
                  </p>
                </div>
              </div>
              {expandedCompetitor === competitor.id ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </button>

          <div className="px-6">
            <CompetitorResearchEvidence competitor={competitor} companyId={companyId} />
          </div>

          {expandedCompetitor === competitor.id && (
            <div className="px-6 pb-6 border-t border-gray-100">
              <DetailedAnalysisForm
                competitor={competitor}
                onUpdateCompetitor={onUpdateCompetitor}
                onInputFocus={onInputFocus}
                onInputBlur={onInputBlur}
                onInputChange={onInputChange}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CompetitorResearchEvidence({
  competitor,
  companyId,
}: {
  competitor: Competitor;
  companyId: string;
}) {
  const research = competitor.aiInsights?.research;
  if (!research || (research.origin !== 'ai' && !research.stale)) return null;
  const stale = isCompetitorResearchStale(competitor);
  const originalDate =
    research.assessedAt || competitor.aiInsights?.lastAnalyzed || competitor.lastScrapedAt;
  const date =
    originalDate && !Number.isNaN(Date.parse(originalDate))
      ? new Date(originalDate).toLocaleDateString('en-AU', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          timeZone: 'Australia/Sydney',
        })
      : null;
  const references = Array.isArray(research.sourceRefs) ? [...new Set(research.sourceRefs)] : [];
  const links = references.filter((reference) => {
    try {
      return ['http:', 'https:'].includes(new URL(reference).protocol);
    } catch {
      return false;
    }
  });
  const companyEvidenceCount = references.length - links.length;
  return (
    <aside
      aria-label={`${competitor.name} research evidence`}
      className={`mb-6 p-4 rounded-lg border text-sm ${stale ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-blue-50 border-blue-200 text-blue-900'}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <strong>
          {stale ? 'Needs reassessment · historical AI research' : 'AI research & evidence'}
        </strong>
        <span className="text-xs">
          {date ? `Research date: ${date}` : 'Research date unavailable'}
        </span>
      </div>
      <p className="mt-2">
        {stale
          ? 'Earlier AI interpretations need reassessment. Saved values remain editable; positioning entered directly can still appear in the current chart.'
          : 'AI positioning is an interpretation of the cited evidence. Unknown positions remain unassessed.'}
      </p>
      <details className="mt-3">
        <summary className="cursor-pointer font-medium">
          {stale ? 'View original research evidence' : 'View research evidence'}
        </summary>
        <p className="mt-2 whitespace-pre-wrap break-words">
          {research.rationale || 'No research rationale recorded.'}
        </p>
        {research.confidence && (
          <p className="mt-2">Original AI confidence: {research.confidence}</p>
        )}
        {!!links.length && (
          <ul className="mt-2 space-y-1 list-disc pl-5">
            {links.map((url) => (
              <li key={url}>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline break-words"
                >
                  {url}
                </a>
              </li>
            ))}
          </ul>
        )}
        {!!companyEvidenceCount && (
          <p className="mt-2">
            <a href={`/company/${companyId}/context`} className="underline">
              Company brief evidence ({companyEvidenceCount})
            </a>
          </p>
        )}
        {!references.length && <p className="mt-2">No research sources recorded.</p>}
      </details>
    </aside>
  );
}

// Detailed Analysis Form Component
function DetailedAnalysisForm({
  competitor,
  onUpdateCompetitor,
  onInputFocus,
  onInputBlur,
  onInputChange,
}: {
  competitor: Competitor;
  onUpdateCompetitor: (id: string, field: string, value: any) => void;
  onInputFocus: () => void;
  onInputBlur: () => void;
  onInputChange: () => void;
}) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mt-6">
      {/* Rival Strategies */}
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-blue-100">
            <Briefcase className="h-5 w-5 text-blue-600" />
          </div>
          <h4 className="text-lg font-semibold text-gray-900">Rival Strategies</h4>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How is your competitor currently competing in the market? (e.g., on price, performance
              etc.)
            </label>
            <textarea
              value={competitor.strategy || ''}
              onChange={(e) => {
                onUpdateCompetitor(competitor.id, 'strategy', e.target.value);
                onInputChange();
              }}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              rows={3}
              placeholder="Describe their competitive approach, pricing strategy, market positioning..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What is the basis for your competitor's competitive advantage? (e.g., key talent,
              large resource pool, customer loyalty etc.)
            </label>
            <textarea
              value={competitor.capabilities || ''}
              onChange={(e) => {
                onUpdateCompetitor(competitor.id, 'capabilities', e.target.value);
                onInputChange();
              }}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              rows={3}
              placeholder="Key talent, resources, customer loyalty, technology, brand strength..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What sort of investments is it making? (e.g., moved to larger office space, recruiting
              more staff, purchased new equipment etc.)
            </label>
            <textarea
              value={competitor.investments || ''}
              onChange={(e) => {
                onUpdateCompetitor(competitor.id, 'investments', e.target.value);
                onInputChange();
              }}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              rows={3}
              placeholder="Facilities, staff, equipment, R&D, acquisitions, expansion..."
            />
          </div>
        </div>
      </div>

      {/* Rival Objectives */}
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-green-100">
            <Target className="h-5 w-5 text-green-600" />
          </div>
          <h4 className="text-lg font-semibold text-gray-900">Rival Objectives</h4>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What is your competitor's current market share?
            </label>
            <textarea
              value={competitor.marketPosition || ''}
              onChange={(e) => {
                onUpdateCompetitor(competitor.id, 'marketPosition', e.target.value);
                onInputChange();
              }}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              rows={3}
              placeholder="Market share, position in industry, competitive ranking..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To what extent is your competitor achieving its financial and strategic objectives?
            </label>
            <textarea
              value={competitor.objectives || ''}
              onChange={(e) => {
                onUpdateCompetitor(competitor.id, 'objectives', e.target.value);
                onInputChange();
              }}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              rows={3}
              placeholder="Financial performance, strategic goal achievement, growth metrics..."
            />
          </div>
        </div>
      </div>

      {/* Rival Capabilities */}
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-orange-100">
            <Brain className="h-5 w-5 text-orange-600" />
          </div>
          <h4 className="text-lg font-semibold text-gray-900">Rival Capabilities</h4>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What are your competitor's key capabilities and are there signals of its efforts to
              acquire new capabilities?
            </label>
            <textarea
              value={competitor.capabilityAnalysis || ''}
              onChange={(e) => {
                onUpdateCompetitor(competitor.id, 'capabilityAnalysis', e.target.value);
                onInputChange();
              }}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              rows={4}
              placeholder="Core capabilities, new skill acquisition, strategic moves..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Note: Your competitor's capabilities serve a strong signal of its future strategic
              actions and reactions to your company's moves.
            </p>
          </div>
        </div>
      </div>

      {/* Rival Assumptions */}
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-purple-100">
            <Users className="h-5 w-5 text-purple-600" />
          </div>
          <h4 className="text-lg font-semibold text-gray-900">Rival Assumptions</h4>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Noting that how a company's senior managers think about their strategic situation can
              have a big impact on how they behave. How do your competitor's senior managers behave?
            </label>
            <textarea
              value={competitor.assumptions || ''}
              onChange={(e) => {
                onUpdateCompetitor(competitor.id, 'assumptions', e.target.value);
                onInputChange();
              }}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              rows={4}
              placeholder="e.g., Always professional, positive attitude, go the extra mile, puts the company first etc."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
